import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDebounce } from 'use-debounce';
import Button from '../components/button/Button';
import Card from '../components/card/Card';
import H from '../components/texts/h/H';
import P from '../components/texts/p/P';
import Input from '../components/input/Input';
import Table from '../components/table/table';
import Icon from '../components/icon/Icon';
import Image from '../components/image/Image';
import TableSkeleton from '../components/TableSkeleton/TableSkeleton';
import DownloadAnnexModal from '../components/modals/DownloadAnnexModal';
import { ITableData } from '../types/interfaces';

import { getAnnexes, AnnexItem } from '../services/AnnexesService';
import { routes } from '../routes/routePaths';

const Annexes = () => {
  const navigate = useNavigate();
  const name = localStorage.getItem('name');
  const [annexes, setAnnexes] = useState<AnnexItem[]>([]);
  const [tableData, setTableData] = useState<ITableData>();
  const [page, setPage] = useState<number>(0);
  const [searchText, setSearchText] = useState<string>('');
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [debouncedSearchText] = useDebounce(searchText, 300);
  
  // Estados para el modal de descarga
  const [showDownloadModal, setShowDownloadModal] = useState<boolean>(false);
  const [selectedAnnex, setSelectedAnnex] = useState<AnnexItem | null>(null);

  const lastRequest = useRef(0);

  useEffect(() => {
    setIsLoading(true);
    const requestId = Date.now();
    lastRequest.current = requestId;

    getAnnexes(page, 10, debouncedSearchText)
      .then((response: any) => {
        if (lastRequest.current !== requestId) return;
        
        if (response && response.success && response.data && response.data.content) {
          setTableData({
            size: response.data.size,
            page: response.data.page,
            totalElements: response.data.total_elements,
            totalPages: response.data.total_pages,
          });

          setAnnexes(response.data.content);
        } else {
          setAnnexes([]);
        }
        
        setIsLoading(false);
        setIsSearching(false);
      })
      .catch(() => {
        setAnnexes([]);
        setIsLoading(false);
        setIsSearching(false);
      });
  }, [page, debouncedSearchText]);

  const handleChangePage = (data: number) => {
    if (data < 0 || data === tableData?.totalPages) {
      return;
    }
    setPage(data);
  };

  const handleSearch = () => {
    setIsSearching(true);
    setPage(0);
  };

  const handleViewAnnex = (annexItem: AnnexItem) => {
    navigate(`${routes.annexes}/${annexItem.id}`);
  };

  const handleRunAnnex = (annexItem: AnnexItem) => {
    setSelectedAnnex(annexItem);
    setShowDownloadModal(true);
  };

  const handleCloseDownloadModal = () => {
    setShowDownloadModal(false);
    setSelectedAnnex(null);
  };

  const handleCreateNewAnnex = () => {
    navigate(routes.newAnnex);
  };

  const formatRequirements = (requiresYear: boolean, requiresMonth: boolean) => {
    const requirements = [];
    if (requiresYear) requirements.push('Año');
    if (requiresMonth) requirements.push('Mes');
    return requirements.length > 0 ? requirements.join(', ') : '-';
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    try {
      // Si la fecha viene en formato DD/MM/YYYY, la convertimos a MM/DD/YYYY para que JavaScript la entienda
      if (dateString.includes('/')) {
        const parts = dateString.split('/');
        if (parts.length === 3) {
          // Asumiendo formato DD/MM/YYYY, convertir a MM/DD/YYYY
          const day = parts[0];
          const month = parts[1];
          const year = parts[2];
          const formattedDate = `${month}/${day}/${year}`;
          const date = new Date(formattedDate);
          
          // Verificar si la fecha es válida
          if (!isNaN(date.getTime())) {
            return date.toLocaleDateString('es-ES');
          }
        }
      }
      
      // Si no es formato DD/MM/YYYY, intentar parsearlo directamente
      const date = new Date(dateString);
      if (!isNaN(date.getTime())) {
        return date.toLocaleDateString('es-ES');
      }
      
      // Si no se puede parsear, devolver la fecha original
      return dateString;
    } catch {
      return dateString;
    }
  };

  const columns = [
    {
      name: 'Nombre',
      selector: (item: AnnexItem) => (
        <P type='2' variant='neutral' withMargin={false}>
          {item.name}
        </P>
      ),
    },
    {
      name: 'Requerimientos',
      selector: (item: AnnexItem) => (
        <P type='2' variant='neutral' withMargin={false}>
          {formatRequirements(item.requires_year, item.requires_month)}
        </P>
      ),
    },
    {
      name: 'F. creación',
      selector: (item: AnnexItem) => (
        <P type='2' variant='neutral' withMargin={false}>
          {formatDate(item.created_at)}
        </P>
      ),
    },
    {
      name: 'Acciones',
      selector: (item: AnnexItem) => (
        <Card display='flex' gap='8px' justifyContent='center'>
            <Icon
                name='play'
                width={20}
                height={20}
                cursor='pointer'
                variant='info'
                onClick={() => handleRunAnnex(item)}
            />
            <Icon
                name='view_outline'
                width={24}
                height={24}
                cursor='pointer'
                variant='info'
                onClick={() => handleViewAnnex(item)}
            />
        </Card>
      ),
      center: true,
    },
  ];

  return (
    <Card
      variant='neutral'
      modifier='inverted'
      spacing={'4'}
      height='-webkit-fill-available'
      width='85vw'
    >
      <Card display='flex' justifyContent='space-between'>
        <Card display='flex' alignItems='center'>
          <H withMargin={false} type='1' weight='bold' modifier='strong'>
            Hola, {name}
          </H>
        </Card>
        <Button variant='secondary' onClick={handleCreateNewAnnex}>
          Crear nuevo anexo
        </Button>
      </Card>

      <Card display='flex' justifyContent='space-between' alignItems='center' margin={{ top: '4' }}>
        <Card width='400px' display='flex' gap='16px'>
          <Input
            type='text'
            size='md'
            icons={{ iconLeft: 'search_outline' }}
            placeholder='Buscar por nombre'
            onChange={(e) => setSearchText(e.target.value)}
            onKeyup={() => handleSearch()}
          />
        </Card>
      </Card>

      {isSearching || isLoading ? (
        <Card margin={{ top: '4' }}>
          <TableSkeleton rows={10} columns={columns.length} />
        </Card>
      ) : annexes.length > 0 ? (
        <Card margin={{ top: '4' }} justifyContent='center' alignItems='center'>
          <Table
            columns={columns}
            data={annexes}
            tableData={tableData}
            handleChangePage={(e: any) => handleChangePage(e)}
          />
        </Card>
      ) : searchText !== '' ? (
        <Card
          display='grid'
          justifyContent='center'
          margin={{ top: '11', bottom: '11' }}
          width='53vw'
        >
          <Card display='flex' justifyContent='center' margin={{ bottom: '2' }}>
            <Image name='folder_empty' />
          </Card>
          <P variant='primary' align='center' withMargin={false} weight='bold'>
            No encontramos resultados.
          </P>
          <P variant='primary' align='center'>
            Podés hacer una nueva búsqueda.
          </P>
        </Card>
      ) : (
        <Card
          display='grid'
          justifyContent='center'
          margin={{ top: '11', bottom: '11' }}
          width='53vw'
        >
          <Card display='flex' justifyContent='center' margin={{ bottom: '2' }}>
            <Image name='folder_empty' />
          </Card>
          <P variant='primary' align='center' withMargin={false} weight='bold'>
            No hay anexos disponibles.
          </P>
          <P variant='primary' align='center'>
            Podés crear uno nuevo.
          </P>
          <Button variant='primary' onClick={handleCreateNewAnnex}>
            Crear nuevo anexo
          </Button>
        </Card>
      )}

      {/* Modal de descarga de anexos */}
      {selectedAnnex && selectedAnnex.id && (
        <DownloadAnnexModal
          isOpen={showDownloadModal}
          onClose={handleCloseDownloadModal}
          annexId={selectedAnnex.id}
          annexName={selectedAnnex.name}
          requiresYear={selectedAnnex.requires_year}
          requiresMonth={selectedAnnex.requires_month}
        />
      )}
    </Card>
  );
};

export default Annexes;